import { Puzzle } from 'lucide-react'
import { useEffect, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useNotification,
} from '@maxus/website/components'
import { trpcClient } from '@maxus/website/lib'
import { bridge } from '@maxus/website/lib/bridge'
import { cn } from '@maxus/website/utils'

type BridgeStatus = 'connected' | 'update-available' | 'unavailable'

const bridgeStatusClasses: Record<BridgeStatus, string> = {
  connected: 'bg-status-resolved',
  'update-available': 'bg-yellow-400',
  unavailable: 'bg-status-danger',
}

const bridgeStatusLabels: Record<BridgeStatus, string> = {
  connected: 'Bridge is connected',
  'update-available': 'Bridge update is available',
  unavailable: 'Bridge is unavailable',
}

type BridgeInfo = {
  currentVersion?: string
  latestVersion?: string
  status: BridgeStatus
}

const BRIDGE_UPDATE_CHECK_INTERVAL_MS = 10 * 60 * 1_000

// 程序化触发浏览器下载。
// 注意（Runtime Targets，见包根 README.md）：此处走浏览器 a[download] 下载语义；
// Electron 渲染进程下载落地行为可能不同，接入 Electron 时需经平台适配层处理。
const triggerBrowserDownload = (url: string) => {
  const link = document.createElement('a')
  link.href = url
  link.download = ''
  link.rel = 'noopener'
  link.target = '_blank'
  document.body.appendChild(link)
  link.click()
  link.remove()
}

// 侧边栏底部入口：确认后拉取最新 bridge 安装包的预签名 URL 并触发下载。
export const DownloadBridge = () => {
  const { t } = useTranslation()
  const { notify } = useNotification()
  const bridgeStatus = useSyncExternalStore(bridge.subscribeStatus, bridge.getStatusSnapshot, bridge.getStatusSnapshot)
  const [loading, setLoading] = useState(false)
  const [bridgeInfo, setBridgeInfo] = useState<BridgeInfo>()

  useEffect(
    () => {
      if (bridgeStatus === 'disconnected') {
        setBridgeInfo({ status: 'unavailable' })
        return
      }

      let disposed = false
      let timeoutId: number | undefined

      const checkUpdate = async () => {
        try {
          const capabilities = await bridge.getCapabilities()
          if (!disposed) {
            const currentVersion = capabilities.bridge_version
            try {
              const { version: latestVersion } = await trpcClient.project.bridge.latestVersion.query()
              if (!disposed) {
                setBridgeInfo({
                  currentVersion,
                  latestVersion,
                  status: currentVersion === latestVersion ? 'connected' : 'update-available',
                })
              }
            } catch {
              if (!disposed) setBridgeInfo({ currentVersion, status: 'connected' })
            }
          }
        } catch {
          if (!disposed) setBridgeInfo({ status: 'unavailable' })
        } finally {
          if (!disposed) {
            timeoutId = window.setTimeout(
              () => void checkUpdate(),
              BRIDGE_UPDATE_CHECK_INTERVAL_MS,
            )
          }
        }
      }

      void checkUpdate()
      return () => {
        disposed = true
        if (timeoutId !== undefined) window.clearTimeout(timeoutId)
      }
    },
    [bridgeStatus],
  )

  const runDownload = async () => {
    if (loading) return
    setLoading(true)
    try {
      const { url } = await trpcClient.project.bridge.download.query()
      triggerBrowserDownload(url)
      notify({ title: t('Started downloading the new plugin version.') })
    } catch (error) {
      console.error('[bridge-download] request failed', error)
      notify({
        status: 'error',
        title: t('Failed to get Bridge download link. Please try again.'),
        description: error instanceof Error ? error.message : undefined,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-label={t('Download Bridge plugin')}
          className={cn(
            'relative grid size-10 place-items-center rounded-lg transition text-text-tertiary hover:bg-surface-panel hover:text-text-secondary',
          )}
          type="button"
        >
          <Puzzle className="size-6" />
          {bridgeInfo && (
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute right-1 top-1 size-2 rounded-full ring-2 ring-surface-chrome',
                bridgeStatusClasses[bridgeInfo.status],
              )}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 space-y-3" side="right">
        <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
          <span
            className={cn(
              'size-2 rounded-full',
              bridgeInfo ? bridgeStatusClasses[bridgeInfo.status] : 'bg-status-neutral',
            )}
          />
          {t(bridgeInfo ? bridgeStatusLabels[bridgeInfo.status] : 'Checking Bridge status')}
        </div>
        <div className="space-y-1 text-xs text-text-tertiary">
          <div className="flex justify-between gap-4">
            <span>{t('Current Bridge version')}</span>
            <span className="font-medium text-text-secondary">{bridgeInfo?.currentVersion ?? '—'}</span>
          </div>
          {bridgeInfo?.latestVersion && (
            <div className="flex justify-between gap-4">
              <span>{t('Latest Bridge version')}</span>
              <span className="font-medium text-text-secondary">{bridgeInfo.latestVersion}</span>
            </div>
          )}
        </div>
        {(bridgeInfo?.latestVersion || bridgeInfo?.status === 'unavailable') && (
          <Button className="w-full" disabled={loading} onClick={() => void runDownload()} size="sm">
            {loading ? t('Downloading') : t('Download')}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  )
}
